import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { FormatoDigitalService } from '../../../services/formato-digital/formato-digital.service';
import { FormatoDigital } from '../../../domain/formato-digital.domain';

@Component({
  selector: 'app-formato-digital-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ToggleSwitchModule,
    ButtonModule,
    InputNumberModule,
    SelectModule
  ],
  templateUrl: './formato-digital-edit.component.html',
  styleUrls: ['./formato-digital-edit.component.scss']
})
export class FormatoDigitalEditComponent implements OnInit, OnDestroy {
  formatoForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  formatoId: string | null = null;
  formato = signal<FormatoDigital | null>(null);

  tipoOptions = [
    { label: 'Banner', value: 'BANNER' },
    { label: 'Post', value: 'POST' },
    { label: 'Story', value: 'STORY' },
    { label: 'Reel', value: 'REEL' }
  ];

  private destroy$ = new Subject<void>();
  private formatoDigitalService = inject(FormatoDigitalService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.formatoId) {
          this.loadFormato();
        }
      });
  }

  initForm(): void {
    this.formatoForm = this.fb.group({
      codigo: ['', Validators.required],
      nombre: ['', Validators.required],
      tipo: [null, Validators.required],
      ancho: [null],
      alto: [null],
      active: [true]
    });

    this.formatoForm.get('tipo')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyBannerValidators();
      });
  }

  private applyBannerValidators(): void {
    const tipo = this.formatoForm.get('tipo')?.value;
    const ancho = this.formatoForm.get('ancho');
    const alto = this.formatoForm.get('alto');

    if (tipo === 'BANNER') {
      ancho?.setValidators([Validators.required, Validators.min(1)]);
      alto?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      ancho?.clearValidators();
      alto?.clearValidators();
    }

    ancho?.updateValueAndValidity({ emitEvent: false });
    alto?.updateValueAndValidity({ emitEvent: false });
  }

  loadFormato(): void {
    const newFormatoId = this.route.snapshot.paramMap.get('id');

    if (newFormatoId !== this.formatoId) {
      this.formatoForm.reset({ active: true });
      this.formato.set(null);
    }

    this.formatoId = newFormatoId;

    if (this.formatoId && this.formatoId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.formatoDigitalService.getById(this.formatoId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (formato: FormatoDigital) => {
            this.loading.set(false);
            this.formato.set(formato);
            this.formatoForm.patchValue({
              codigo: formato.codigo || '',
              nombre: formato.nombre || '',
              tipo: formato.tipo || null,
              ancho: formato.ancho ?? null,
              alto: formato.alto ?? null,
              active: formato.active ?? true
            });
            this.applyBannerValidators();
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error al cargar formato digital:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al cargar los datos del formato digital',
              life: 3000
            });
          }
        });
    } else {
      this.isEditMode.set(false);
    }
  }

  onSubmit(): void {
    if (this.formatoForm.invalid) {
      this.markFormGroupTouched(this.formatoForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.formatoForm.value;

    const formato: FormatoDigital = {
      codigo: formValue.codigo,
      nombre: formValue.nombre,
      tipo: formValue.tipo,
      ancho: formValue.ancho ?? null,
      alto: formValue.alto ?? null,
      active: formValue.active ?? true
    };

    if (this.isEditMode()) {
      formato.id = Number(this.formatoId);
    }

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.formatoDigitalService.update(this.formatoId!, formato)
      : this.formatoDigitalService.create(formato);

    operation
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: this.isEditMode() ? 'Formato digital actualizado correctamente' : 'Formato digital creado correctamente',
            life: 3000
          });
          setTimeout(() => {
            this.router.navigate(['/formatos-digitales']);
          }, 1500);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al guardar formato digital:', error);

          let errorMessage = 'Error al guardar el formato digital';
          if (error.error?.violations) {
            const violations = error.error.violations;
            errorMessage = violations.map((v: any) => v.message).join(', ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      });
  }

  onCancel(): void {
    this.router.navigate(['/formatos-digitales']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Formato Digital' : 'Nuevo Formato Digital';
  }
}
